/**
 * View-state — per-user, per-section sort + filter selections.
 *
 * The shape of `filters` and `sort` varies by section, so the API
 * stores them as a generic `unknown` jsonb blob server-side. Each
 * frontend section validates its own slice with its own zod schema
 * (see e.g. apps/web/src/routes/(app)/history/view-schemas/).
 *
 * The server's only job: round-trip the blob with cross-device sync
 * (last-write-wins via `clientUpdatedAt`) and gate it by a per-section
 * `schemaVersion` so stale clients can be ignored cleanly.
 *
 * Plan §UX Notes / View State.
 */

import { z } from 'zod';

/** Set of section keys recognised by the server. Adding a new
 *  section means appending here + on the frontend's schema registry. */
export const ViewStateSectionKey = z.enum([
  'favorites',
  'inbox',
  'liked-you',
  'you-liked',
  'visited-you',
  'great-match',
  'outside-filters',
  // Outbound mirror of 'outside-filters' — viewer's likes that
  // sit beyond their own filter window.
  'explore-beyond-filters',
  'my-activity',
  'chat-list',
  'search',
  // Self-managed Activity rails. Read-only records of the viewer's
  // own outbound actions; sort-only (no filter fields) by design.
  'passed',
  'blocked',
  'reported',
]);
export type ViewStateSectionKey = z.infer<typeof ViewStateSectionKey>;

/** A view-state blob is opaque to the server. The frontend per-section
 *  schema validates the inner shape on read; the server only enforces
 *  that the blob is JSON-serialisable and not absurdly large. */
export const ViewStateBlob = z
  .object({
    filters: z.unknown(),
    sort: z.unknown(),
  })
  .strict();
export type ViewStateBlob = z.infer<typeof ViewStateBlob>;

export const ViewStateResponse = z.object({
  sectionKey: ViewStateSectionKey,
  state: ViewStateBlob.nullable(),
  schemaVersion: z.number().int().min(1),
  updatedAt: z.string().nullable(),
});
export type ViewStateResponse = z.infer<typeof ViewStateResponse>;

export const PatchViewStateBody = z.object({
  state: ViewStateBlob,
  schemaVersion: z.number().int().min(1),
  /** Client's wall-clock when the change was made. Server picks
   *  max(clientUpdatedAt, now()) to avoid a slow clock overwriting
   *  a fresher server row. */
  clientUpdatedAt: z.string().datetime({ offset: true }),
});
export type PatchViewStateBody = z.infer<typeof PatchViewStateBody>;

export const PatchViewStateResponse = z.object({
  ok: z.literal(true),
  updatedAt: z.string(),
});
export type PatchViewStateResponse = z.infer<typeof PatchViewStateResponse>;
